import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CheckCircle, Calendar, Clock, User, Phone, DollarSign, ArrowLeft } from 'lucide-react';
import api from '../utils/api';

export default function PaymentSuccessPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [appointment, setAppointment] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) {
      loadAppointment();
    }
  }, [id]);

  const loadAppointment = async () => {
    try {
      const response = await api.get(`/appointment/${id}`);
      if (response.data.success && response.data.appointment) {
        setAppointment(response.data.appointment);
      }
    } catch (error) {
      console.error('Erro ao carregar agendamento:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (!appointment) {
    return (
      <div className="min-h-screen bg-dark-900 flex items-center justify-center px-4">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Agendamento não encontrado</h1>
          <button
            onClick={() => navigate('/')}
            className="btn-primary"
          >
            Voltar para Início
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark-900 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-green-500 rounded-full mb-4">
            <CheckCircle className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">
            Pagamento Confirmado!
          </h1>
          <p className="text-gray-400">
            Seu agendamento foi realizado com sucesso
          </p>
        </div>

        {/* Card de Confirmação */}
        <div className="card p-6 mb-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-white">Detalhes do Agendamento</h2>
            <span className="px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-sm font-medium">
              Pago
            </span>
          </div>

          <div className="space-y-4">
            {/* Cliente */}
            <div className="flex items-center gap-3">
              <User className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-400">Cliente</p>
                <p className="text-white font-medium">{appointment.nome_cliente}</p>
              </div>
            </div>

            {/* Telefone */}
            <div className="flex items-center gap-3">
              <Phone className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-400">Telefone</p>
                <p className="text-white font-medium">{appointment.telefone}</p>
              </div>
            </div>

            {/* Data e Horário */}
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-400">Data</p>
                  <p className="text-white font-medium">
                    {new Date(appointment.data).toLocaleDateString('pt-BR', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric'
                    })}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-400">Horário</p>
                  <p className="text-white font-medium">{appointment.horario}</p>
                </div>
              </div>
            </div>

            {/* Serviço */}
            {appointment.servico_nome && (
              <div className="flex items-center gap-3">
                <DollarSign className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-400">Serviço</p>
                  <p className="text-white font-medium">{appointment.servico_nome}</p>
                  {appointment.servico_preco && (
                    <p className="text-primary-400 font-medium">
                      R$ {parseFloat(appointment.servico_preco).toFixed(2)}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Informações Importantes */}
        <div className="card bg-blue-500/10 border border-blue-500/30 p-6 mb-6">
          <h3 className="text-lg font-semibold text-white mb-3">
            Informações Importantes
          </h3>
          <ul className="space-y-2 text-gray-300">
            <li className="flex items-start gap-2">
              <span className="text-blue-400 mt-1">•</span>
              <span>Chegue com 10 minutos de antecedência</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-400 mt-1">•</span>
              <span>Leve seu documento de identificação</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-400 mt-1">•</span>
              <span>Em caso de atraso, entre em contato com a barbearia</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-400 mt-1">•</span>
              <span>Cancelamentos devem ser feitos com pelo menos 2 horas de antecedência</span>
            </li>
          </ul>
        </div>

        {/* Botões de Ação */}
        <div className="flex flex-col sm:flex-row gap-4">
          <button
            onClick={() => navigate('/')}
            className="btn-primary flex-1"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar para Início
          </button>
          
          <button
            onClick={() => window.location.href = `https://wa.me/55${appointment.telefone?.replace(/\D/g, '')}`}
            className="btn-secondary flex-1"
          >
            <Phone className="w-4 h-4 mr-2" />
            Contato
          </button>
        </div>
      </div>
    </div>
  );
}
